import { supabase } from './supabase'
import type { SignUpInput, SignInInput, AuthUser } from '@/types/auth'
import { z } from 'zod'

const getAuthEnv = () => {
  const url = import.meta.env.VITE_SUPABASE_URL as string
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string
  if (!url || !key) throw new Error('Missing Supabase env (VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY)')
  return { url: url.replace(/\/$/, ''), key }
}

// Password validation schema
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')

// Sign up validation schema
export const signUpSchema = z
  .object({
    email: z.string().email('Invalid email address'),
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

export const authHelpers = {
  async signUp({ email, password }: SignUpInput) {
    const normalizedEmail = String(email).trim().toLowerCase()
    const normalizedPassword = String(password).trim()
    const validation = passwordSchema.safeParse(normalizedPassword)
    if (!validation.success) {
      throw new Error(validation.error.errors[0].message)
    }
    
    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password: normalizedPassword,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    })
    
    if (error) throw error
    return data
  },

  async signIn({ email, password }: SignInInput) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    
    if (error) throw error
    return data
  },

  /**
   * Sign in via the token API and setSession. Use this when the client's signInWithPassword
   * promise may hang after a 200 response (e.g. Supabase client bug); we get the session
   * and set it ourselves so redirect can happen immediately.
   */
  async signInWithTokenApi({ email, password }: SignInInput) {
    const { url, key } = getAuthEnv()
    const normalizedEmail = String(email).trim().toLowerCase()
    const normalizedPassword = String(password).trim()
    const res = await fetch(`${url}/auth/v1/token?grant_type=password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: key,
        Authorization: `Bearer ${key}`,
      },
      body: JSON.stringify({ email: normalizedEmail, password: normalizedPassword }),
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok) {
      const err = json as { error_description?: string; msg?: string; error?: string }
      const code = (res.headers.get('x-sb-error-code') ?? err?.error) ?? ''
      const isInvalidCreds =
        res.status === 400 &&
        (code === 'invalid_credentials' || String(err?.error_description ?? err?.msg ?? '').toLowerCase().includes('invalid'))
      if (isInvalidCreds) {
        // Token API can reject while the official client succeeds (e.g. same request, different handling). Try once.
        try {
          await Promise.race([
            authHelpers.signIn({ email: normalizedEmail, password: normalizedPassword }),
            new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), 8000)),
          ])
          const session = await authHelpers.getSession()
          if (session) {
            return { session, user: (await supabase.auth.getUser()).data.user }
          }
        } catch {
          // fall through to throw helpful message
        }
        throw new Error(
          'Invalid email or password. If you’ve already confirmed your email, use “Forgot password” below to set a new password and try again.'
        )
      }
      throw new Error(err?.error_description ?? err?.msg ?? `Sign-in failed (${res.status})`)
    }
    const access_token = (json as { access_token?: string }).access_token
    const refresh_token = (json as { refresh_token?: string }).refresh_token
    if (!access_token || !refresh_token) {
      throw new Error('Invalid response from sign-in')
    }
    const SET_SESSION_TIMEOUT_MS = 5000
    const setSessionPromise = supabase.auth.setSession({ access_token, refresh_token })
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('setSession_timeout')), SET_SESSION_TIMEOUT_MS)
    )
    try {
      const { error } = await Promise.race([setSessionPromise, timeoutPromise])
      if (error) throw error
      const session = await Promise.race([
        authHelpers.getSession(),
        new Promise<null>((r) => setTimeout(() => r(null), 1500)),
      ])
      if (session) {
        return { session, user: (await supabase.auth.getUser()).data.user }
      }
    } catch (e) {
      if ((e as Error)?.message !== 'setSession_timeout') throw e
    }
    // setSession or getSession hung; write session to storage so app has it, then caller can navigate
    const hostname = new URL(url).hostname
    const storageKey = `sb-${hostname.split('.')[0]}-auth-token`
    const expires_in = (json as { expires_in?: number }).expires_in ?? 3600
    const expires_at = Math.floor(Date.now() / 1000) + expires_in
    const userFromJson = (json as { user?: unknown }).user
    let user = userFromJson
    if (!user && access_token) {
      try {
        const payload = JSON.parse(atob(access_token.split('.')[1]!))
        user = { id: payload.sub, email: payload.email ?? '', email_confirmed_at: payload.email_confirmed_at }
      } catch {
        user = { id: '', email: '', email_confirmed_at: null }
      }
    }
    const session = {
      access_token,
      refresh_token,
      expires_in,
      expires_at,
      token_type: 'bearer' as const,
      user,
    }
    try {
      localStorage.setItem(storageKey, JSON.stringify(session))
    } catch {
      /* ignore */
    }
    return { session, user, reloadRequired: true }
  },

  async signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  },

  /**
   * Request a password reset email. Uses a client-side timeout so we don't hang on
   * Supabase 504 (recover sends the email synchronously via SMTP; slow SMTP → timeout).
   */
  async resetPasswordRequest(email: string) {
    const RESET_REQUEST_TIMEOUT_MS = 28_000
    const requestPromise = supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('RESET_PASSWORD_REQUEST_TIMEOUT')), RESET_REQUEST_TIMEOUT_MS)
    )

    const { error } = await Promise.race([requestPromise, timeoutPromise])
    if (error) throw error
  },

  /** Client-side timeout (e.g. 28s) so we don't hang on Supabase 504 Gateway Timeout. */
  async updatePassword(newPassword: string) {
    const UPDATE_PASSWORD_TIMEOUT_MS = 28_000
    const updatePromise = supabase.auth.updateUser({ password: newPassword })
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('UPDATE_PASSWORD_TIMEOUT')), UPDATE_PASSWORD_TIMEOUT_MS)
    )

    const result = await Promise.race([updatePromise, timeoutPromise])
    const { error } = result
    if (error) throw error
  },

  async resendVerificationEmail() {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.email) throw new Error('No user email found')
    
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: user.email,
    })
    
    if (error) throw error
  },

  async getSession() {
    const { data: { session }, error } = await supabase.auth.getSession()
    if (error) throw error
    return session
  },

  async getUser(): Promise<AuthUser | null> {
    const { data: { user }, error } = await supabase.auth.getUser()
    if (error) throw error
    
    if (!user) return null
    
    return {
      id: user.id,
      email: user.email!,
      emailVerified: !!user.email_confirmed_at,
      createdAt: user.created_at,
      lastSignInAt: user.last_sign_in_at || null,
    }
  },
}
